import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.ts';
import { StartModeField, type StartMode } from '../../components/StartModeField.tsx';

export function AdminRaceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const race = useQuery({
    queryKey: ['admin-race', id],
    queryFn: () => api.raceDetails(id!),
    enabled: !!id,
  });
  const boats = useQuery({ queryKey: ['boats', false], queryFn: () => api.listBoats(false) });
  const allClasses = useQuery({ queryKey: ['classes', false], queryFn: () => api.listClasses(false) });
  const [showPostpone, setShowPostpone] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  if (race.isLoading) return <p className="text-slate-500">Laster…</p>;
  if (race.isError || !race.data) return <p className="text-red-400">Klarte ikke laste regattaen</p>;

  const { race: r, entries, classes } = race.data;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/admin/races" className="text-xs text-slate-400">&larr; Tilbake</Link>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-xl font-bold">{r.name}</h1>
          <Link to={`/r/${r.slug}`} target="_blank" className="text-xs text-sky-400">
            Se offentlig side ↗
          </Link>
        </div>
        <div className="text-sm text-slate-400 font-mono">
          {new Date(r.startTime).toLocaleString('no-NO', {
            dateStyle: 'medium',
            timeStyle: 'short',
            timeZone: 'Europe/Oslo',
          })}
        </div>
        {r.status === 'postponed' && (
          <div className="mt-2 text-amber-300 text-sm">
            ⚠️ Utsatt{r.postponeReason && ` — ${r.postponeReason}`}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          <span>
            Startklokke:{' '}
            <span className="text-slate-300">
              {r.startMode === 'manual'
                ? 'båtene starter klokka selv'
                : r.startMode === 'committee'
                  ? 'komiteen starter klokka'
                  : 'fast starttid'}
            </span>
          </span>
          {r.registrationDeadline && (
            <span>
              Påmeldingsfrist:{' '}
              <span className="text-slate-300">
                {new Date(r.registrationDeadline).toLocaleString('no-NO', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                  timeZone: 'Europe/Oslo',
                })}
              </span>
            </span>
          )}
          {classes.length > 0 ? (
            <span>
              Tillatte klasser:{' '}
              <span className="text-slate-300">{classes.map((c) => c.name).join(', ')}</span>
            </span>
          ) : (
            <span className="italic">Åpen for alle klasser</span>
          )}
        </div>
      </div>

      {/* Handlinger */}
      <div className="flex flex-wrap gap-2">
        {(r.status === 'scheduled' || r.status === 'postponed') && (
          <button
            onClick={() => setShowPostpone(!showPostpone)}
            className="px-3 py-2 bg-amber-700 hover:bg-amber-600 rounded text-sm"
          >
            {showPostpone ? 'Avbryt' : 'Utsett regatta'}
          </button>
        )}
        {r.status === 'postponed' && (
          <button
            onClick={() => setShowResume(!showResume)}
            className="px-3 py-2 bg-sky-700 hover:bg-sky-600 rounded text-sm"
          >
            {showResume ? 'Avbryt' : 'Angi ny starttid'}
          </button>
        )}
        {(r.status === 'started' || r.status === 'scheduled' || r.status === 'postponed') && (
          <button
            onClick={async () => {
              if (confirm('Marker regattaen som ferdig?')) {
                await api.finalizeRace(r.id);
                qc.invalidateQueries({ queryKey: ['admin-race', id] });
              }
            }}
            className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm"
          >
            Marker som ferdig
          </button>
        )}
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded text-sm"
        >
          {showSettings ? 'Skjul innstillinger' : 'Innstillinger'}
        </button>
      </div>

      {showSettings && (
        <SettingsForm
          raceId={r.id}
          currentStartTime={r.startTime}
          currentDeadline={r.registrationDeadline}
          currentRegistrationUrl={r.registrationUrl ?? null}
          currentStartMode={r.startMode ?? 'fixed'}
          currentClassIds={classes.map((c) => c.classId)}
          currentCourseId={r.courseId ?? null}
          allClasses={allClasses.data ?? []}
          onDone={() => {
            setShowSettings(false);
            qc.invalidateQueries({ queryKey: ['admin-race', id] });
            qc.invalidateQueries({ queryKey: ['admin-races'] });
          }}
        />
      )}

      {showPostpone && (
        <PostponeForm
          raceId={r.id}
          onDone={() => {
            setShowPostpone(false);
            qc.invalidateQueries({ queryKey: ['admin-race', id] });
            qc.invalidateQueries({ queryKey: ['admin-races'] });
          }}
        />
      )}

      {showResume && (
        <ResumeForm
          raceId={r.id}
          onDone={() => {
            setShowResume(false);
            qc.invalidateQueries({ queryKey: ['admin-race', id] });
            qc.invalidateQueries({ queryKey: ['admin-races'] });
          }}
        />
      )}

      {/* Påmelding */}
      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
        <h2 className="font-semibold mb-3">Påmeldte båter ({entries.length})</h2>

        <AddEntryForm
          raceId={r.id}
          boats={boats.data ?? []}
          existingBoatIds={entries.map((e) => e.boatId)}
          allowedClassIds={classes.map((c) => c.classId)}
          onAdded={() => qc.invalidateQueries({ queryKey: ['admin-race', id] })}
        />

        <ul className="mt-3 divide-y divide-slate-800 border border-slate-800 rounded-lg overflow-hidden">
          {entries.map((e) => (
            <EntryRow key={e.id} entry={e} onChange={() => qc.invalidateQueries({ queryKey: ['admin-race', id] })} />
          ))}
          {entries.length === 0 && (
            <li className="p-3 text-center text-slate-500 text-sm">Ingen påmeldte enda.</li>
          )}
        </ul>
      </section>

      {/* Faresone — permanent sletting */}
      <DangerZone
        raceId={r.id}
        raceName={r.name}
        entryCount={entries.length}
        onDeleted={() => {
          qc.invalidateQueries({ queryKey: ['admin-races'] });
          qc.removeQueries({ queryKey: ['admin-race', id] });
          navigate('/admin/races');
        }}
      />
    </div>
  );
}

function DangerZone({
  raceId,
  raceName,
  entryCount,
  onDeleted,
}: {
  raceId: string;
  raceName: string;
  entryCount: number;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: async () => api.deleteRace(raceId),
    onSuccess: () => onDeleted(),
    onError: (e: ApiError | Error) => setErr(e.message),
  });

  // Tolerant match: ignorer ledende/etterfølgende og doble mellomrom + store/små bokstaver,
  // så navn med f.eks. dobbelt mellomrom likevel kan bekreftes.
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  const canDelete = norm(confirmText) === norm(raceName) && raceName.trim().length > 0;

  return (
    <section className="bg-red-950/20 border border-red-900/60 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-red-300">Faresone</h2>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            className="text-xs px-3 py-1.5 bg-red-900/40 hover:bg-red-900/60 border border-red-800 text-red-200 rounded"
          >
            Slett regatta permanent…
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-red-200">
            Dette sletter regattaen og <strong>alle {entryCount} påmeldinger</strong> permanent.
            Resultater og historikk går tapt. Handlingen kan <strong>ikke angres</strong>.
          </p>
          <p className="text-sm text-slate-300">
            Skriv inn regattaens navn <span className="font-mono text-red-200">{raceName}</span>{' '}
            for å bekrefte:
          </p>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={raceName}
            className="w-full px-3 py-2 bg-slate-950 border border-red-800 rounded-lg font-mono text-sm"
          />
          {err && (
            <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">
              {err}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => {
                setOpen(false);
                setConfirmText('');
                setErr(null);
              }}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm"
            >
              Avbryt
            </button>
            <button
              onClick={() => del.mutate()}
              disabled={!canDelete || del.isPending}
              className="px-3 py-2 bg-red-700 hover:bg-red-600 rounded text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {del.isPending ? 'Sletter…' : 'Ja, slett permanent'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function AddEntryForm({
  raceId,
  boats,
  existingBoatIds,
  allowedClassIds,
  onAdded,
}: {
  raceId: string;
  boats: {
    id: string;
    sailNumber: string;
    boatName: string;
    skipperName: string;
    className?: string | null;
    classId?: string | null;
  }[];
  existingBoatIds: string[];
  allowedClassIds: string[];
  onAdded: () => void;
}) {
  const [boatId, setBoatId] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: async () => {
      if (!boatId) throw new Error('Velg en båt');
      return api.addEntry(raceId, boatId);
    },
    onSuccess: () => {
      setBoatId('');
      setErr(null);
      onAdded();
    },
    onError: (e: ApiError | Error) => setErr(e.message),
  });

  // Regattaen kan begrense tillatte klasser. Tom liste = åpen for alle klasser.
  const classFiltered =
    allowedClassIds.length === 0
      ? boats
      : boats.filter((b) => b.classId && allowedClassIds.includes(b.classId));
  const available = classFiltered.filter((b) => !existingBoatIds.includes(b.id));

  return (
    <div>
      <div className="flex gap-2">
        <select
          value={boatId}
          onChange={(e) => setBoatId(e.target.value)}
          className="flex-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm"
        >
          <option value="">— Velg båt å melde på —</option>
          {available.map((b) => (
            <option key={b.id} value={b.id}>
              {b.sailNumber} • {b.boatName}
              {b.className ? ` [${b.className}]` : ''} ({b.skipperName})
            </option>
          ))}
        </select>
        <button
          onClick={() => add.mutate()}
          disabled={!boatId || add.isPending}
          className="px-4 bg-sky-600 hover:bg-sky-500 rounded text-sm font-semibold disabled:opacity-50"
        >
          Meld på
        </button>
      </div>
      {err && <div className="text-xs text-red-300 mt-2">{err}</div>}
      {allowedClassIds.length > 0 && (
        <div className="text-xs text-slate-500 mt-2">
          Kun båter i regattaens tillatte klasser vises.
        </div>
      )}
    </div>
  );
}

/** ISO-tidspunkt → lokale dato-/tid-deler for date/time-inputfelt. */
function isoToLocalParts(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function SettingsForm({
  raceId,
  currentStartTime,
  currentDeadline,
  currentRegistrationUrl,
  currentStartMode,
  currentClassIds,
  currentCourseId,
  allClasses,
  onDone,
}: {
  raceId: string;
  currentStartTime: string;
  currentDeadline: string | null;
  currentRegistrationUrl: string | null;
  currentStartMode: StartMode;
  currentClassIds: string[];
  currentCourseId: string | null;
  allClasses: { id: string; name: string }[];
  onDone: () => void;
}) {
  const courses = useQuery({ queryKey: ['courses'], queryFn: () => api.listCourses() });
  const [courseId, setCourseId] = useState(currentCourseId ?? '');
  const startParts = isoToLocalParts(currentStartTime);
  const [startDate, setStartDate] = useState(startParts.date);
  const [startTime, setStartTime] = useState(startParts.time);
  const initialDate = currentDeadline
    ? new Date(currentDeadline).toISOString().slice(0, 10)
    : '';
  const initialTime = currentDeadline
    ? new Date(currentDeadline).toLocaleTimeString('en-GB', { hour12: false }).slice(0, 5)
    : '';
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [classIds, setClassIds] = useState<string[]>(currentClassIds);
  const [registrationUrl, setRegistrationUrl] = useState(currentRegistrationUrl ?? '');
  const [startMode, setStartMode] = useState<StartMode>(currentStartMode);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (!startDate || !startTime) throw new Error('Starttidspunkt må fylles ut');
      const start = new Date(`${startDate}T${startTime}`);
      if (isNaN(start.getTime())) throw new Error('Ugyldig starttidspunkt');

      let registrationDeadline: string | null = null;
      if (date && time) {
        const dl = new Date(`${date}T${time}`);
        if (isNaN(dl.getTime())) throw new Error('Ugyldig påmeldingsfrist');
        registrationDeadline = dl.toISOString();
      } else if (date || time) {
        throw new Error('Sett både dato og tid for frist, eller la begge stå tomme');
      }
      return api.updateRace(raceId, {
        startTime: start.toISOString(),
        registrationDeadline,
        registrationUrl: registrationUrl.trim() || null,
        startMode,
        allowedClassIds: classIds,
        courseId: courseId || null,
      });
    },
    onSuccess: onDone,
    onError: (e: ApiError | Error) => setErr(e.message),
  });

  function toggleClass(id: string) {
    setClassIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h3 className="font-semibold">Innstillinger</h3>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">
          {err}
        </div>
      )}

      <div>
        <label className="block text-xs text-slate-400 mb-1">Starttidspunkt</label>
        <p className="text-xs text-slate-500 mb-2">
          Endrer dato og klokkeslett for selve starten. (For å utsette med AP-flagg, bruk «Utsett
          regatta» over — dette endrer bare tiden.)
        </p>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            required
            className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1">Påmeldingsfrist (registrar)</label>
        <p className="text-xs text-slate-500 mb-2">
          Etter denne kan kun admin etterregistrere. La feltene stå tomme for å holde åpent.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </div>
      </div>

      <StartModeField value={startMode} onChange={setStartMode} />

      <div>
        <label className="block text-xs text-slate-400 mb-1">Påmeldingslenke</label>
        <p className="text-xs text-slate-500 mb-2">
          Vises som «Påmelding»-knapp på den offentlige regattalista. La stå tom for ingen lenke.
        </p>
        <input
          type="url"
          value={registrationUrl}
          onChange={(e) => setRegistrationUrl(e.target.value)}
          placeholder="https://…"
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1">Tillatte klasser</label>
        <p className="text-xs text-slate-500 mb-2">
          Velg én eller flere klasser. Tom → åpen for alle klasser.
        </p>
        <div className="grid grid-cols-2 gap-1">
          {allClasses.map((c) => {
            const checked = classIds.includes(c.id);
            return (
              <label
                key={c.id}
                className={`flex items-center gap-2 px-2 py-1.5 border rounded cursor-pointer text-sm ${
                  checked
                    ? 'border-sky-600 bg-sky-900/30 text-sky-200'
                    : 'border-slate-700 bg-slate-950 text-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleClass(c.id)}
                  className="accent-sky-500"
                />
                {c.name}
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1">Bane</label>
        <select
          value={courseId}
          onChange={(e) => setCourseId(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="">— Ingen bane —</option>
          {(courses.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-500 mt-1">
          Merker og start-/mållinje fra banen vises på live-kart og reprise. Lag baner under «Baner».
        </p>
      </div>

      <button
        type="submit"
        disabled={save.isPending}
        className="w-full bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
      >
        {save.isPending ? 'Lagrer…' : 'Lagre innstillinger'}
      </button>
    </form>
  );
}

function EntryRow({
  entry,
  onChange,
}: {
  entry: {
    id: string;
    sailNumber: string;
    boatName: string;
    skipperName: string;
    className?: string | null;
    ownerUsername?: string | null;
    position: number | null;
    finishNotes: string | null;
  };
  onChange: () => void;
}) {
  const [position, setPosition] = useState<string>(entry.position?.toString() ?? '');
  const [notes, setNotes] = useState<string>(entry.finishNotes ?? '');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.updateEntry(entry.id, {
        position: position ? parseInt(position, 10) : null,
        finishNotes: notes || null,
      });
      onChange();
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="p-3 bg-slate-900/30">
      <div className="flex items-center gap-3 mb-2">
        <div className="font-mono text-xs text-slate-400 w-16">{entry.sailNumber}</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate flex items-center gap-2">
            {entry.boatName}
            {entry.className && (
              <span className="text-xs bg-slate-700/40 text-slate-300 px-2 py-0.5 rounded">
                {entry.className}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 truncate">
            {entry.skipperName}
            {entry.ownerUsername && (
              <span className="text-slate-500"> • eier: {entry.ownerUsername}</span>
            )}
          </div>
        </div>
        <button
          onClick={async () => {
            if (confirm(`Fjerne ${entry.boatName} fra regattaen?`)) {
              await api.removeEntry(entry.id);
              onChange();
            }
          }}
          className="text-xs text-red-400"
        >
          Fjern
        </button>
      </div>
      <div className="flex gap-2">
        <input
          type="number"
          min="1"
          placeholder="Plass"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
          className="w-20 px-2 py-1.5 bg-slate-950 border border-slate-700 rounded text-sm"
        />
        <input
          placeholder="DNF/DNS/DSQ"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="flex-1 px-2 py-1.5 bg-slate-950 border border-slate-700 rounded text-sm"
        />
        <button
          onClick={save}
          disabled={saving}
          className="px-3 bg-emerald-600 hover:bg-emerald-500 rounded text-sm font-semibold disabled:opacity-50"
        >
          {saving ? '…' : 'Lagre'}
        </button>
      </div>
    </li>
  );
}

function PostponeForm({ raceId, onDone }: { raceId: string; onDone: () => void }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');
  const [later, setLater] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      let newStartTime: string | undefined;
      if (!later) {
        if (!date || !time) throw new Error('Fyll inn dato og tid, eller kryss av for "sett tid senere"');
        newStartTime = new Date(`${date}T${time}`).toISOString();
      }
      return api.postponeRace(raceId, { newStartTime, reason: reason || undefined });
    },
    onSuccess: onDone,
    onError: (e: ApiError | Error) => setErr(e.message),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate();
      }}
      className="bg-amber-900/20 border border-amber-700 rounded-xl p-4 space-y-3"
    >
      <h3 className="font-semibold">Utsett regatta</h3>
      {err && <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">{err}</div>}

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={later} onChange={(e) => setLater(e.target.checked)} />
        Sett ny tid senere (vises som "UTSATT" til du angir)
      </label>

      {!later && (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </div>
      )}

      <input
        placeholder="Begrunnelse (valgfritt) — f.eks. vindstille, ikke nok vind"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
      />

      <button
        type="submit"
        disabled={mut.isPending}
        className="w-full bg-amber-600 hover:bg-amber-500 py-3 rounded font-semibold disabled:opacity-60"
      >
        {mut.isPending ? 'Utsetter…' : 'Utsett'}
      </button>
    </form>
  );
}

function ResumeForm({ raceId, onDone }: { raceId: string; onDone: () => void }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (!date || !time) throw new Error('Fyll inn dato og tid');
      const newStartTime = new Date(`${date}T${time}`).toISOString();
      return api.resumeRace(raceId, newStartTime);
    },
    onSuccess: onDone,
    onError: (e: ApiError | Error) => setErr(e.message),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate();
      }}
      className="bg-sky-900/20 border border-sky-700 rounded-xl p-4 space-y-3"
    >
      <h3 className="font-semibold">Angi ny starttid</h3>
      {err && <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">{err}</div>}

      <div className="grid grid-cols-2 gap-2">
        <input
          type="date"
          required
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
        <input
          type="time"
          required
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>

      <button
        type="submit"
        disabled={mut.isPending}
        className="w-full bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
      >
        {mut.isPending ? 'Setter…' : 'Sett ny starttid og gjenoppta'}
      </button>
    </form>
  );
}
